import React, { useState, useEffect, useCallback } from "react";
import { api, fmtNumber } from "@/lib/api";
import { logError } from "@/lib/logger";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ShieldCheck, Loader2, Users, Activity, KeyRound, RefreshCw, Trash2, UserCheck, UserX, Lock, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import AdminSupport from "@/components/AdminSupport";

function StatCard({ icon: Icon, label, value, active, onClick, testId }) {
  return (
    <Card
      data-testid={testId}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick?.();
        }
      }}
      className={`cursor-pointer transition-colors hover:border-primary/50 ${
        active ? "border-primary ring-1 ring-primary/40 bg-primary/5" : ""
      }`}
    >
      <CardContent className="pt-5 flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
          <Icon className="w-5 h-5 text-primary" />
        </div>
        <div>
          <p className="text-xs text-muted-foreground uppercase tracking-wide">{label}</p>
          <p className="text-2xl font-semibold text-foreground">{fmtNumber(value || 0)}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch (e) {
    return iso;
  }
}

const EVENT_LABELS = {
  registered: "Registered",
  logged_in: "Logged in",
  connected_hubspot: "Connected HubSpot",
  generated_report: "Generated AI report",
  queried_data: "Queried data",
  changed_password: "Changed password",
  saved_key: "Saved HubSpot key",
  forgot_key: "Forgot HubSpot key",
  admin_set_status: "Admin changed status",
  admin_reset_password: "Admin reset password",
  admin_delete_user: "Admin deleted user",
  support_ticket_created: "Support request",
  support_ticket_public: "Support request (public)",
  support_admin_reply: "Support reply sent",
  support_admin_forward: "Support forwarded",
  support_admin_delete: "Support ticket deleted",
};

// Stat-card metadata (keyed by the drilldown `metric` the backend expects).
const STAT_CARDS = [
  { metric: "users", label: "Users", icon: Users, statKey: "total_users" },
  { metric: "events", label: "Activity Events", icon: Activity, statKey: "total_events" },
  { metric: "hubspot_connections", label: "HubSpot Connects", icon: KeyRound, statKey: "hubspot_connections" },
  { metric: "reports_generated", label: "Reports Generated", icon: Activity, statKey: "reports_generated" },
  { metric: "query_data", label: "Query Data", icon: MessageSquare, statKey: "query_data" },
];

// Inline drilldown panel shown under the stat cards. Renders the detail rows
// behind the clicked metric (users table or a metric-specific events table).
function StatDrilldown({ metric, label, loading, data, onClose }) {
  const rows = (data && data.rows) || [];
  return (
    <Card data-testid="admin-stat-drilldown">
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <CardTitle className="text-base">
            {label} <span className="text-xs font-normal text-muted-foreground ml-1">· {rows.length} shown</span>
          </CardTitle>
          <Button data-testid="admin-stat-drilldown-close" onClick={onClose} variant="outline" size="sm">
            Close
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
          </div>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8" data-testid="admin-stat-drilldown-empty">
            No data yet for {label}.
          </p>
        ) : (
          <div className="overflow-x-auto max-h-[420px] overflow-y-auto" data-testid="admin-stat-drilldown-table">
            {metric === "users" ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Logins</TableHead>
                    <TableHead>Last login</TableHead>
                    <TableHead>Created</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((u) => (
                    <TableRow key={u.id}>
                      <TableCell className="font-medium text-foreground">{u.email}</TableCell>
                      <TableCell>
                        {u.role === "admin" ? <Badge className="bg-primary hover:bg-primary">Admin</Badge> : <Badge variant="outline">User</Badge>}
                      </TableCell>
                      <TableCell>
                        {u.status === "active" ? (
                          <Badge className="bg-green-600 hover:bg-green-600">Active</Badge>
                        ) : (
                          <Badge variant="secondary">Suspended</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">{fmtNumber(u.login_count || 0)}</TableCell>
                      <TableCell className="text-muted-foreground text-sm">{fmtDate(u.last_login_at)}</TableCell>
                      <TableCell className="text-muted-foreground text-sm">{fmtDate(u.created_at)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    {metric === "events" && <TableHead>Event</TableHead>}
                    <TableHead>User</TableHead>
                    {metric === "hubspot_connections" && <TableHead>Portal</TableHead>}
                    {metric === "hubspot_connections" && <TableHead className="text-right">Deals</TableHead>}
                    {metric === "query_data" && <TableHead>Question</TableHead>}
                    <TableHead className="text-right">When</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((ev) => (
                    <TableRow key={ev.id}>
                      {metric === "events" && (
                        <TableCell>
                          <Badge variant="outline" className="text-[10px]">{EVENT_LABELS[ev.event] || ev.event}</Badge>
                        </TableCell>
                      )}
                      <TableCell className="text-foreground/90">{ev.email || "—"}</TableCell>
                      {metric === "hubspot_connections" && (
                        <TableCell className="text-muted-foreground text-sm">{ev.detail?.portal_id || "—"}</TableCell>
                      )}
                      {metric === "hubspot_connections" && (
                        <TableCell className="text-right text-muted-foreground">
                          {typeof ev.detail?.deals === "number" ? fmtNumber(ev.detail.deals) : "—"}
                        </TableCell>
                      )}
                      {metric === "query_data" && (
                        <TableCell className="max-w-[360px]">
                          <span className="block truncate text-foreground/90" title={ev.detail?.question_preview || ""}>
                            {ev.detail?.question_preview || "—"}
                          </span>
                        </TableCell>
                      )}
                      <TableCell className="text-right text-xs text-muted-foreground">{fmtDate(ev.at)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function AdminPortal({ currentUser }) {
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [events, setEvents] = useState([]);
  const [stats, setStats] = useState(null);
  const [busyId, setBusyId] = useState(null);

  // "Change my password" (self-service) dialog state.
  const [pwOpen, setPwOpen] = useState(false);
  const [curPw, setCurPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [pwSaving, setPwSaving] = useState(false);

  // Stat-card drilldown state.
  const [activeMetric, setActiveMetric] = useState(null); // which card is expanded
  const [drill, setDrill] = useState(null); // { metric, kind, rows, total }
  const [drillLoading, setDrillLoading] = useState(false);

  const selectMetric = async (metric) => {
    // Toggle: clicking the active card collapses the panel.
    if (activeMetric === metric) {
      setActiveMetric(null);
      setDrill(null);
      return;
    }
    setActiveMetric(metric);
    setDrill(null);
    setDrillLoading(true);
    try {
      const res = await api.adminStatsDrilldown(metric);
      setDrill(res);
    } catch (err) {
      logError("Failed to load drilldown:", err);
      toast.error(err.response?.data?.detail || "Could not load details.");
      setDrill({ metric, kind: "error", rows: [], total: 0 });
    } finally {
      setDrillLoading(false);
    }
  };

  const changeMyPassword = async () => {
    if (newPw.length < 6) {
      toast.error("New password must be at least 6 characters.");
      return;
    }
    if (newPw !== confirmPw) {
      toast.error("New password and confirmation do not match.");
      return;
    }
    setPwSaving(true);
    try {
      await api.changePassword(curPw, newPw);
      toast.success("Your password has been changed.");
      setPwOpen(false);
      setCurPw("");
      setNewPw("");
      setConfirmPw("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not change password.");
    } finally {
      setPwSaving(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, e, s] = await Promise.all([
        api.adminUsers().catch(() => ({ users: [] })),
        api.adminEvents().catch(() => ({ events: [] })),
        api.adminStats().catch(() => null),
      ]);
      setUsers(u.users || []);
      setEvents(e.events || []);
      setStats(s);
    } catch (err) {
      logError("Failed to load admin data:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const setStatus = async (u, status) => {
    setBusyId(u.id);
    try {
      await api.adminSetStatus(u.id, status);
      toast.success(`${u.email} ${status === "suspended" ? "suspended" : "reactivated"}.`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed.");
    } finally {
      setBusyId(null);
    }
  };

  const resetPassword = async (u) => {
    const pw = window.prompt(`Set a new password for ${u.email} (min 6 chars):`);
    if (pw === null) return;
    if (pw.length < 6) {
      toast.error("Password must be at least 6 characters.");
      return;
    }
    setBusyId(u.id);
    try {
      await api.adminResetPassword(u.id, pw);
      toast.success(`Password reset for ${u.email}.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset failed.");
    } finally {
      setBusyId(null);
    }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Delete ${u.email}? This cannot be undone.`)) return;
    setBusyId(u.id);
    try {
      await api.adminDeleteUser(u.id);
      toast.success(`${u.email} deleted.`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed.");
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-portal">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-primary" />
          <div>
            <h2 className="font-semibold text-foreground leading-tight">Admin Portal</h2>
            <p className="text-xs text-muted-foreground">Manage subscribers and monitor platform activity.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="admin-change-my-password" onClick={() => setPwOpen(true)} variant="outline" size="sm">
            <Lock className="w-4 h-4 mr-1.5" /> Change my password
          </Button>
          <Button data-testid="admin-refresh" onClick={load} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
          </Button>
        </div>
      </div>

      {/* Stats — each card is clickable to reveal a drilldown below the row */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4" data-testid="admin-stats-row">
        {STAT_CARDS.map((c) => (
          <StatCard
            key={c.metric}
            testId={`admin-stat-${c.metric}`}
            icon={c.icon}
            label={c.label}
            value={stats?.[c.statKey]}
            active={activeMetric === c.metric}
            onClick={() => selectMetric(c.metric)}
          />
        ))}
      </div>

      {/* Inline drilldown panel for the active stat card */}
      {activeMetric && (
        <StatDrilldown
          metric={activeMetric}
          label={(STAT_CARDS.find((c) => c.metric === activeMetric) || {}).label}
          loading={drillLoading}
          data={drill}
          onClose={() => {
            setActiveMetric(null);
            setDrill(null);
          }}
        />
      )}

      {/* Users table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Subscribers ({users.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto" data-testid="admin-users-table">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Logins</TableHead>
                  <TableHead>Last login</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => {
                  const isAdmin = u.role === "admin";
                  const busy = busyId === u.id;
                  return (
                    <TableRow key={u.id} data-testid="admin-user-row">
                      <TableCell className="font-medium text-foreground max-w-[220px]">
                        <span className="truncate block" title={u.email}>{u.email}</span>
                      </TableCell>
                      <TableCell>
                        {isAdmin ? <Badge className="bg-primary hover:bg-primary">Admin</Badge> : <Badge variant="outline">User</Badge>}
                      </TableCell>
                      <TableCell>
                        {u.status === "active" ? (
                          <Badge className="bg-green-600 hover:bg-green-600">Active</Badge>
                        ) : (
                          <Badge variant="secondary">Suspended</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">{fmtNumber(u.login_count || 0)}</TableCell>
                      <TableCell className="text-muted-foreground text-sm">{fmtDate(u.last_login_at)}</TableCell>
                      <TableCell className="text-muted-foreground text-sm">{fmtDate(u.created_at)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          {/* Suspend/reactivate + delete are NON-ADMIN only
                              (the backend blocks changing/deleting admins). */}
                          {!isAdmin && (
                            <>
                              {u.status === "active" ? (
                                <Button size="sm" variant="outline" disabled={busy} onClick={() => setStatus(u, "suspended")} title="Suspend">
                                  <UserX className="w-3.5 h-3.5" />
                                </Button>
                              ) : (
                                <Button size="sm" variant="outline" disabled={busy} onClick={() => setStatus(u, "active")} title="Reactivate">
                                  <UserCheck className="w-3.5 h-3.5" />
                                </Button>
                              )}
                            </>
                          )}
                          {/* Reset password is available for ALL users, incl. admins. */}
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={busy}
                            data-testid="admin-reset-password-btn"
                            onClick={() => resetPassword(u)}
                            title={isAdmin ? "Reset admin password" : "Reset password"}
                          >
                            <KeyRound className="w-3.5 h-3.5" />
                          </Button>
                          {!isAdmin && (
                            <Button size="sm" variant="outline" disabled={busy} onClick={() => deleteUser(u)} title="Delete" className="text-red-600 hover:text-red-600">
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Activity feed */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Activity &amp; Data Use ({events.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {events.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">No activity yet.</p>
          ) : (
            <div className="space-y-1.5 max-h-[420px] overflow-y-auto" data-testid="admin-events-feed">
              {events.map((ev) => (
                <div key={ev.id} className="flex items-center justify-between text-sm border-b border-border/60 py-1.5">
                  <span className="flex items-center gap-2 min-w-0">
                    <Badge variant="outline" className="text-[10px]">{EVENT_LABELS[ev.event] || ev.event}</Badge>
                    <span className="text-foreground/80 truncate">{ev.email || "—"}</span>
                    {ev.detail?.portal_id ? <span className="text-xs text-muted-foreground">portal {ev.detail.portal_id}</span> : null}
                    {typeof ev.detail?.deals === "number" ? <span className="text-xs text-muted-foreground">· {ev.detail.deals} deals</span> : null}
                  </span>
                  <span className="text-xs text-muted-foreground flex-shrink-0 ml-2">{fmtDate(ev.at)}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Support requests inbox */}
      <AdminSupport />

      {/* Change-my-password dialog (self-service for the logged-in admin) */}
      <Dialog open={pwOpen} onOpenChange={setPwOpen}>
        <DialogContent className="max-w-md" data-testid="admin-change-password-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5 text-primary" /> Change my password
            </DialogTitle>
            <DialogDescription>
              Update the password for your account{currentUser?.email ? ` (${currentUser.email})` : ""}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="cur-pw">Current password</Label>
              <Input
                id="cur-pw"
                data-testid="admin-cur-pw"
                type="password"
                value={curPw}
                onChange={(e) => setCurPw(e.target.value)}
                placeholder="Your current password"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new-pw">New password</Label>
              <Input
                id="new-pw"
                data-testid="admin-new-pw"
                type="password"
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                placeholder="At least 6 characters"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="confirm-pw">Confirm new password</Label>
              <Input
                id="confirm-pw"
                data-testid="admin-confirm-pw"
                type="password"
                value={confirmPw}
                onChange={(e) => setConfirmPw(e.target.value)}
                placeholder="Re-enter the new password"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwOpen(false)} disabled={pwSaving}>
              Cancel
            </Button>
            <Button onClick={changeMyPassword} disabled={pwSaving} data-testid="admin-change-password-submit">
              {pwSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Change password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
